import {assert} from 'Asserts';
import {processTimingEvent} from 'performance/BenchlingProfiler';

import {ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_REGISTRY_EVENTS_VERSION = 1;

export enum ProfilerRegistryFeatures {
  TRIAL_SAMPLE_VALIDATION = 'trialSampleValidation',
  TRIAL_SAMPLE_IMPORT = 'trialSampleImport',
  TRIAL_SAMPLE_IMPORT_TRAY_TASK = 'trialSampleImportTrayTask',
}

enum ProfilerRegistryActions {
  IMPORT_STARTED = 'importStarted',
  IMPORT_ENDED = 'importEnded',
  VALIDATION_STARTED = 'validationStarted',
  VALIDATION_ENDED = 'validationEnded',
  TRAY_TASK_VISIBLE = 'trayTaskVisible',
}

export const TRIAL_SAMPLE_VALIDATION_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REGISTRY,
  feature: ProfilerRegistryFeatures.TRIAL_SAMPLE_VALIDATION,
  action: ProfilerRegistryActions.VALIDATION_STARTED,
};
export const TRIAL_SAMPLE_VALIDATION_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REGISTRY,
  feature: ProfilerRegistryFeatures.TRIAL_SAMPLE_VALIDATION,
  action: ProfilerRegistryActions.VALIDATION_ENDED,
};
export const TRIAL_SAMPLE_IMPORT_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REGISTRY,
  feature: ProfilerRegistryFeatures.TRIAL_SAMPLE_IMPORT,
  action: ProfilerRegistryActions.IMPORT_STARTED,
};
export const TRIAL_SAMPLE_IMPORT_FINISHED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REGISTRY,
  feature: ProfilerRegistryFeatures.TRIAL_SAMPLE_IMPORT,
  action: ProfilerRegistryActions.IMPORT_ENDED,
};
export const TRIAL_SAMPLE_IMPORT_STARTED_FOR_TRAY_TASK_EVENT = {
  domain: ProfilerEventDomain.REGISTRY,
  feature: ProfilerRegistryFeatures.TRIAL_SAMPLE_IMPORT_TRAY_TASK,
  action: ProfilerRegistryActions.IMPORT_STARTED,
};
export const TRIAL_SAMPLE_IMPORT_TRAY_TASK_VISIBLE_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.REGISTRY,
  feature: ProfilerRegistryFeatures.TRIAL_SAMPLE_IMPORT_TRAY_TASK,
  action: ProfilerRegistryActions.TRAY_TASK_VISIBLE,
};

function processTrialSampleValidation(profilerFinishedEvent: ProfilerEvent): void {
  const {id} = profilerFinishedEvent;
  processTimingEvent(
    {...TRIAL_SAMPLE_VALIDATION_STARTED_EVENT, id},
    {...TRIAL_SAMPLE_VALIDATION_FINISHED_EVENT, id},
    'Trial Samples Validated',
    'perf.validate_trial_samples',
    PROFILER_REGISTRY_EVENTS_VERSION
  );
}
function processTrialSampleImport(profilerFinishedEvent: ProfilerEvent): void {
  const {id} = profilerFinishedEvent;
  processTimingEvent(
    {...TRIAL_SAMPLE_IMPORT_STARTED_EVENT, id},
    {...TRIAL_SAMPLE_IMPORT_FINISHED_EVENT, id},
    'Trial Samples Imported',
    'perf.import_trial_samples',
    PROFILER_REGISTRY_EVENTS_VERSION
  );
}

function processTrialSampleImportTrayTask(profilerFinishedEvent: ProfilerEvent): void {
  const {id} = profilerFinishedEvent;
  processTimingEvent(
    {...TRIAL_SAMPLE_IMPORT_STARTED_FOR_TRAY_TASK_EVENT, id},
    {...TRIAL_SAMPLE_IMPORT_TRAY_TASK_VISIBLE_EVENT, id},
    'Trial Samples Import Tray Task Visible',
    'perf.import_trial_samples_tray_task_visible',
    PROFILER_REGISTRY_EVENTS_VERSION
  );
}
const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerRegistryFeatures]: Function} = {
  [ProfilerRegistryFeatures.TRIAL_SAMPLE_VALIDATION]: processTrialSampleValidation,
  [ProfilerRegistryFeatures.TRIAL_SAMPLE_IMPORT]: processTrialSampleImport,
  [ProfilerRegistryFeatures.TRIAL_SAMPLE_IMPORT_TRAY_TASK]: processTrialSampleImportTrayTask,
};

export function processRegistryEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerRegistryFeatures;
  assert(
    Object.values(ProfilerRegistryFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature](profilerEvent);
}
